import axios from "axios";

import {
  obterRefreshToken,
  useSessaoStore,
  type ParDeTokens,
} from "../auth/store";

/*
 * Single-flight do refresh (I0 — a peça crítica do CP-C; ADR 0026 §Invariantes).
 *
 * RAZÃO DE EXISTIR (servico.py:435 — rotação por família com reuse-detection
 * ESTRITA, SEM grace period): se N requests recebem 401 ao mesmo tempo e cada
 * uma dispara seu próprio POST /api/auth/refresh, a 1ª rotaciona e CONSOME o
 * refresh; as demais reapresentam o MESMO refresh já usado → o backend detecta
 * reuso e REVOGA A FAMÍLIA INTEIRA → logout imediato. Logo: para N 401s
 * simultâneos, EXATAMENTE 1 chamada de refresh; as outras aguardam a mesma
 * promise e dão replay com o token novo.
 *
 * DESENHO:
 *  - `promessaEmVoo` é a ÚNICA promise de refresh viva a nível de módulo. Quem
 *    chega durante a janela apenas a aguarda (não dispara outra).
 *  - A chamada de refresh usa um cliente axios CRU (`axios`, sem nossa instância
 *    nem o interceptor de 401-retry) → impossível recursão infinita pelo próprio
 *    fluxo de refresh.
 *  - Reset em TODOS os caminhos (sucesso E falha) via `.finally`, senão um
 *    refresh falho deixaria `promessaEmVoo` preso e travaria os próximos.
 */

/** Shape EXATO do wire de `POST /api/auth/refresh` (snake_case do backend). */
interface RespostaRefresh {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  token_type: string;
}

/**
 * Única promise de refresh em voo. `null` quando não há refresh em andamento.
 * Visível só ao módulo; o interceptor a consome via `refreshComSingleFlight`.
 */
let promessaEmVoo: Promise<string> | null = null;

/**
 * Executa o POST /api/auth/refresh CRU (sem interceptor de retry), troca o par
 * no store e devolve o novo access. Lança se o refresh estiver ausente ou se o
 * backend rejeitar (401 = família revogada / refresh inválido).
 */
async function executarRefresh(): Promise<string> {
  const refreshAtual = obterRefreshToken();
  if (refreshAtual === null) {
    throw new Error("sem refresh token para rotacionar");
  }

  // Cliente CRU: `axios.post` direto, NÃO a instância com interceptor. Sem
  // baseURL da instância → caminho absoluto explícito para o proxy do Vite.
  const resposta = await axios.post<RespostaRefresh>("/api/auth/refresh", {
    refresh_token: refreshAtual,
  });

  const dados = resposta.data;
  // Boundary do parse: mapeia o wire (snake_case) para o ParDeTokens.
  const par: ParDeTokens = {
    access_token: dados.access_token,
    refresh_token: dados.refresh_token,
    expires_in: dados.expires_in,
  };
  useSessaoStore.getState().definirSessao(par);
  return par.access_token;
}

/**
 * Ponto de entrada do single-flight. Se já há refresh em voo, devolve a MESMA
 * promise (não dispara outro). Caso contrário, inicia um e garante o reset da
 * promise em voo no fim (sucesso ou falha).
 *
 * Em caso de falha (ex.: 401 no refresh), a promise rejeita e o chamador
 * (interceptor) é responsável por limpar o store + sinalizar sessão expirada —
 * a responsabilidade fica no interceptor para manter este módulo focado no
 * mecanismo single-flight.
 */
export function refreshComSingleFlight(): Promise<string> {
  if (promessaEmVoo !== null) {
    return promessaEmVoo;
  }
  promessaEmVoo = executarRefresh().finally(() => {
    // Reset em TODOS os caminhos: um próximo 401 dispara um NOVO refresh, nunca
    // reusa a promise já resolvida/rejeitada.
    promessaEmVoo = null;
  });
  return promessaEmVoo;
}

/** Há refresh em voo? Exposto para teste do invariante I0(d). */
export function refreshEmVoo(): boolean {
  return promessaEmVoo !== null;
}
